import React, { useEffect, useState } from "react";
import "../../assets/css/table/Pagination.css";
import ChevronDown from "../Atomos/Icons/ChevronDown";
import { IPagination2, ISorting } from "../../interfaces/Components/ITable";
import { TABLE_PAGE_SIZE_OPTIONS } from "../../constants/tablePagination";

interface ContentPaginationControlProps {
  pagination: IPagination2;
  setPagination: (pagination: IPagination2) => void;
  totalCount: number;
  sort?: ISorting;
  setSort?: (sort: ISorting) => void;
  sortableFields?: string[];
}

const ContentPaginationControl: React.FC<ContentPaginationControlProps> = ({
  pagination,
  setPagination,
  totalCount,
}) => {
  const [disablePagination, setDisablePagination] = useState<{
    previousPage: boolean;
    nextPage: boolean;
  }>({ previousPage: true, nextPage: true });

  const getVisiblePages = (
    currentPage: number,
    totalPages: number
  ): (number | string)[] => {
    const pages: (number | string)[] = [];
    const delta = 1;

    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);

    if (left > 1) {
      pages.push(1);
      if (left > 2) {
        pages.push("...");
      }
    }

    for (let i = left; i <= right; i++) {
      pages.push(i);
    }

    if (right < totalPages) {
      if (right < totalPages - 1) {
        pages.push("...");
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const visiblePages = getVisiblePages(
    pagination.pageIndex + 1,
    Math.ceil(totalCount / pagination.pageSize)
  );

  useEffect(() => {
    setDisablePagination({
      previousPage: pagination.pageIndex <= 0,
      nextPage: pagination.pageIndex + 1 >= Math.ceil(totalCount / pagination.pageSize),
    });
  }, [pagination, totalCount]);

  const handleRowsPerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPagination({ ...pagination, pageSize: Number(e.target.value) });
  };

  const handlePage = (page: number) => {
    setPagination({ ...pagination, pageIndex: page });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="pagination pt-6">
        <label htmlFor="records-per-page" className="records-per-page">
          Rows per Page
        </label>
        <div className="relative flex justify-center items-center bg-gray-25">
          <select
            id="records-per-page"
            name="records-per-page"
            className="selectorPageSize"
            value={pagination.pageSize}
            onChange={handleRowsPerPage}
          >
            {TABLE_PAGE_SIZE_OPTIONS.map((pageSize) => (
              <option
                key={pageSize}
                value={pageSize}
                className="whitespace-nowrap"
              >
                {pageSize}
              </option>
            ))}
          </select>

          <div className="absolute h-6 w-6 right-2 top-2 fill-gray-font pointer-events-none">
            <ChevronDown />
          </div>
        </div>

        <button
          className={`arrow ${disablePagination.previousPage && "!text-light-gray"}`}
          onClick={() => handlePage(pagination.pageIndex - 1)}
          disabled={disablePagination.previousPage}
        >
          &lt;
        </button>

        {visiblePages.map((page, index) => (
          <button
            key={index}
            className={`paginationButton ${
              page === pagination.pageIndex + 1 ? "currentButton bg-primary" : ""
            }`}
            onClick={() => typeof page === "number" && handlePage(page - 1)}
          >
            {page}
          </button>
        ))}

        <button
          className={`arrow ${disablePagination.nextPage && "!text-light-gray"}`}
          onClick={() => handlePage(pagination.pageIndex + 1)}
          disabled={disablePagination.nextPage}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default ContentPaginationControl; 